import React, { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../styles/Auth.css"; 
import { useNotifications } from "./NotificationProvider";
const logo = "/images/Logo.svg"; 

const API = process.env.REACT_APP_API_URL || "";

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const inviteMode = searchParams.get("mode");
  const { showNotification } = useNotifications();
  const [isRegistering, setIsRegistering] = useState(inviteMode === "register");
  const [accountType, setAccountType] = useState("bedienung"); 
  const [companyAction, setCompanyAction] = useState("join"); 
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [firstName, setFirstName] = useState(""); 
  const [lastName, setLastName] = useState(""); 
  const [companyName, setCompanyName] = useState(""); 
  const [companyCode, setCompanyCode] = useState(""); 
  const [message, setMessage] = useState(""); 
  const loginToggleRef = useRef(null);
  const [emailExistsInOtherAccount, setEmailExistsInOtherAccount] = useState(false); 
  //const [existingAccountTypes, setExistingAccountTypes] = useState([]);

  // Validiert die eingegebene E-Mail-Adresse
  const validateEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  // Aktualisiert die E-Mail-Adresse und überprüft auf Fehler
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
  
    if (validateEmail(newEmail)) {
      setEmailError("");
    }
  
    if (emailExistsInOtherAccount) {
      setEmailExistsInOtherAccount(false);
      setMessage("");
      setFirstName("");
      setLastName("");
      setConfirmPassword("");
    }
  };

  // Überprüft, ob die E-Mail bereits verwendet wird
  const handleEmailBlur = async () => {
    if (!isRegistering) return; // Keine Überprüfung beim Login

    if (!validateEmail(email)) {
      setEmailError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    try {
      const response = await fetch(`${API}/api/auth/check-email?email=${encodeURIComponent(email)}`);
      const data = await response.json();

      if (response.ok) {
        if (data.accountTypes.length > 0) {
          //setExistingAccountTypes(data.accountTypes);

          if (data.accountTypes.includes(accountType)) {
            setEmailError("Diese E-Mail ist bereits für diese Rolle registriert. Bitte melden Sie sich an.");
            setEmailExistsInOtherAccount(false);
          } else {
            setMessage(
              `Diese E-Mail ist bereits für ${data.accountTypes
                .map((type) => type.charAt(0).toUpperCase() + type.slice(1))
                .join(", ")} registriert. Sie können sich für eine neue Rolle registrieren, indem Sie sich mit ihrem Passwort anmelden.`
            );            setEmailExistsInOtherAccount(true);
          }
        } else {
          //setExistingAccountTypes([]);
          setMessage(""); 
          setEmailExistsInOtherAccount(false);
        }
      }
    } catch (error) {
      console.error("Fehler beim Überprüfen der E-Mail:", error);
    }
  };

  // Aktualisiert das Passwort und entfernt Fehler, wenn die Bestätigung übereinstimmt
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (isRegistering && confirmPassword === e.target.value) {
      setPasswordError(""); 
    }
  };

  // Überprüft, ob das bestätigte Passwort mit dem eingegebenen Passwort übereinstimmt
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (password === e.target.value) {
      setPasswordError(""); 
    } else {
      setPasswordError("Die eingegebenen Passwörter stimmen nicht überein.");
    }
  };

  // Verarbeitet das Formular-Submit und sendet die Daten an den Server
  const handleSubmit = async (event) => {
    event.preventDefault();
    setEmailError("");
    setPasswordError("");
  
    if (!validateEmail(email)) {
      setEmailError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }
  
    if (isRegistering && password !== confirmPassword && !emailExistsInOtherAccount) {
      setPasswordError("Die eingegebenen Passwörter stimmen nicht überein.");
      return;
    }
  
    const url = isRegistering
      ? `${API}/api/auth/register`
      : `${API}/api/auth/login`;
  
    const payload = { email, password };
  
    if (isRegistering) {
      payload.accountType = accountType;
      payload.emailExistsInOtherAccount = emailExistsInOtherAccount;
      if (inviteToken) payload.inviteToken = inviteToken;

      if (!emailExistsInOtherAccount) {
        payload.firstName = firstName;
        payload.lastName = lastName;
        payload.confirmPassword = confirmPassword;
      }

      if (accountType === "unternehmen") {
        if (companyAction === "create") {
          payload.companyName = companyName;
        } else if (companyAction === "join") {
          payload.companyCode = companyCode;
        }
      }
    } else {
      payload.selectedAccountType = accountType;
    }
  
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
      }
  
      if (isRegistering) {
        if (data.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("accountType", data.accountType || accountType);
          navigate(inviteToken ? `/invite?token=${encodeURIComponent(inviteToken)}` : "/dashboard");
          return;
        }

        if (!emailExistsInOtherAccount) {
          navigate(inviteToken ? `/verify?invite=${encodeURIComponent(inviteToken)}` : "/verify");
        } else {
          setIsRegistering(false);
          setMessage("");
          showNotification("success", "Registrierung erfolgreich. Sie können sich jetzt anmelden.");
          loginToggleRef.current.click();
        }
      } else {
        localStorage.setItem("token", data.token);
        localStorage.setItem("accountType", accountType);
        navigate(inviteToken ? `/invite?token=${encodeURIComponent(inviteToken)}` : "/dashboard");
      }
    } catch (err) {
      console.error("Fehler bei der Anmeldung:", err.message);
      setPasswordError("Anmeldefehler: " + err.message);
    }
  };

  return (
    <div className="auth-container">
        <div className="logo-area">
            <img src={logo} alt="Festzeltfinanzen Logo" className="logo" />
        </div>
        <div className="auth-area">
            <div className="auth-content">
                <h2>{isRegistering ? "Registrieren" : "Login"}</h2>
                <p>{inviteToken
                  ? "Melden Sie sich als Bedienung an, um die Einladung zu prüfen."
                  : (isRegistering ? "Erstellen Sie Ihr Konto" : "Melden Sie sich an")}</p>

                <form onSubmit={handleSubmit}>
                    {inviteToken ? (
                      <div className="invite-account-notice">Bedienungsaccount</div>
                    ) : (
                      <div className="toggle-container" onClick={() => setAccountType(accountType === "bedienung" ? "unternehmen" : "bedienung")}>
                        <div className={`toggle-slider ${accountType}`}></div>
                        <span className={accountType === "bedienung" ? "active" : ""}>Bedienung</span>
                        <span className={accountType === "unternehmen" ? "active" : ""}>Firmenaccount</span>
                      </div>
                    )}
                    
                {isRegistering && !emailExistsInOtherAccount && (
                    <>
                    <input 
                      type="text" 
                      placeholder="Vorname" 
                      value={firstName} 
                      onChange={(e) => setFirstName(e.target.value)} 
                    />
                    <input 
                      type="text" 
                      placeholder="Nachname" 
                      value={lastName} 
                      onChange={(e) => setLastName(e.target.value)} 
                    />
                    </>
                )}

                <input 
                      type="email" 
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="E-Mail-Adresse" 
                      value={email} 
                      onChange={handleEmailChange} 
                      onBlur={handleEmailBlur} 
                      required 
                    />
                    
                <input 
                      type="password" 
                      placeholder="Passwort" 
                      value={password} 
                      onChange={handlePasswordChange} 
                      required 
                    />

                {isRegistering && !emailExistsInOtherAccount && (
                      <>
                        <input 
                          type="password" 
                          placeholder="Passwort bestätigen" 
                          value={confirmPassword} 
                          onChange={handleConfirmPasswordChange} 
                        />
                      </>
                    )}

                {isRegistering && accountType === "unternehmen" && (
                    <>
                    <div className="toggle-container" onClick={() => setCompanyAction(companyAction === "join" ? "create" : "join")}>
                        <div className={`toggle-slider ${companyAction}`}></div>
                        <span className={companyAction === "join" ? "active" : ""}>Firma beitreten</span>
                        <span className={companyAction === "create" ? "active" : ""}>Firma erstellen</span>
                    </div>

                    {companyAction === "create" && (
                        <input 
                          type="text" 
                          placeholder="Firmenname" 
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)} 
                          required 
                        />
                    )}

                    {companyAction === "join" && (
                        <input 
                          type="text" 
                          placeholder="Firmen-ID eingeben" 
                          value={companyCode}
                          onChange={(e) => setCompanyCode(e.target.value)} 
                          required 
                        />
                    )}
                    </>
                )}
                {(emailError || passwordError) && (
                  <p className="error-message">
                    {emailError || passwordError}
                  </p>
                )}
                {message && (
                  <p className="success-message">
                    {message}
                  </p>
                )}
                <button type="submit">{isRegistering ? "Registrieren" : "Login"}</button>
                </form>

                <p>
                {isRegistering ? "Haben Sie bereits ein Konto?" : "Haben Sie noch kein Konto?"}{" "}
                <span ref={loginToggleRef} onClick={() => {setIsRegistering(!isRegistering);
                  setMessage(""); // Nachricht zurücksetzen
                  setEmailError(""); // Falls nötig auch Email-Fehlermeldung zurücksetzen
                  setPasswordError("");
                }} className="toggle-link">
                    {isRegistering ? "Login" : "Registrieren"}
                    
                    </span>
                </p>
            </div>
        </div>
    </div>
  );
};

export default Auth;
